/* eslint-disable no-restricted-syntax */
/* eslint-disable no-eval */
/* eslint-disable no-plusplus */
import moment from 'moment';
import { toast } from 'react-toastify';
import { GLOBAL_VAR_REGEX } from './constants';
import { isValidRut } from './validateRut';

// -------------------------------------------------------------------------------------- //
// --------------------------------- Related with Dates --------------------------------- //
// -------------------------------------------------------------------------------------- //

/**
 * Valida un valor con una lista de validadores.
 *
 * @param {string} value - El valor a validar.
 * @param {Array} validators - Lista de validadores.
 * @returns {object} - Objeto con las propiedades `error` y `msj`.
 *                    `error` indica si hubo un error de validación.
 *                    `msj` contiene el mensaje de error en caso de haberlo.
 */
export const isValidVariableValue = (value, validators = []) => {
  const result = { error: false, msj: null };

  if (validators.length === 0) {
    return result;
  }

  /**
   * Verifica si se cumple el criterio de validación para un validador específico.
   *
   * @param {string} validatorName - Nombre del validador.
   * @param {string} variableValue - Valor a validar.
   * @returns {boolean} - `true` si el valor cumple el criterio de validación, de lo contrario `false`.
   */
  const isValidCriteria = (validatorName, variableValue) => {
    switch (validatorName) {
      case 'RUT Chile':
        // En caso de ser un validador de RUT, se verifica su validez
        return isValidRut(variableValue);
      default:
        // Otros casos no especificados, se consideran válidos
        return true;
    }
  };

  validators.some(
    ({ name: validatorName = '', regular_expression: regExp = '.*', error_message: errorMessage = '' }) => {
      const regex = new RegExp(regExp);
      const isValidFormat = regex.test(value);
      const isValid = isValidFormat && isValidCriteria(validatorName, value);

      if (!isValid) {
        result.error = true;
        result.msj = errorMessage;
        return true; // Sale del bucle some al encontrar un error
      }

      return false; // Continúa iterando en busca de más errores
    }
  );

  return result;
};

/**
 * @param  {String} value
 * @returns {boolean}
 */
export const validateEmptyInputValue = (value = '') => {
  return value.toString().replaceAll(' ', '').length > 0;
};

export const isValidDate = (date) => {
  // An invalid date object returns NaN for getTime() and NaN is the only object not strictly equal to itself.
  // eslint-disable-next-line no-self-compare
  return date.getTime() === date.getTime();
};

const padTo2Digits = (num) => {
  return num.toString().padStart(2, '0');
};

export const formatDate = (data) => {
  // return a string with the folowing format DD-MM-YYYY
  const date = new Date(data);
  if (isValidDate(date)) {
    return [padTo2Digits(date.getDate()), padTo2Digits(date.getMonth() + 1), date.getFullYear()].join('-');
  }
  return data;
};

/*
export const tryParseDate = (dateStr, format) => {
  try {
    // when the format exists it is because it in the previewTask
    if (dateStr && format) {
      let m = moment(dateStr, 'YYYY-MM-DD', true);
      if (m.isValid()) {
        return m.format(format);
      }

      m = moment(dateStr, 'YYYY-MM-DDTHH:mm:ss.SSSSZ', true);
      if (m.isValid()) {
        return m.format(format);
      }

      m = moment(dateStr, 'DD-MM-YYYY', true);
      if (m.isValid()) {
        return m.format(format);
      }

      m = moment(dateStr, 'YYYY-MM-DD HH:mm:ss', true);
      if (m.isValid()) {
        return m.format(format);
      }

      m = moment(dateStr, 'YYYY-MM-DD HH:mm', true);
      if (m.isValid()) {
        return m.format(format);
      }
    }
    if (dateStr) {
      // eslint-disable-next-line no-restricted-globals
      if (dateStr instanceof Date && !isNaN(dateStr)) {
        return dateStr;
      }
      let m = moment(dateStr, 'YYYY-MM-DDTHH:mm:ss.SSSSZ', true);
      if (m.isValid()) {
        return m.toDate();
      }

      m = moment(dateStr, 'YYYY-MM-DD', true);
      if (m.isValid()) {
        return m.toDate();
      }

      m = moment(dateStr, 'DD-MM-YYYY', true);
      if (m.isValid()) {
        return m.toDate();
      }
    }
    return null;
  } catch (error) {
    return null;
  }
}; */

export const tryParseDate = (dateStr, format) => {
  try {
    if (!dateStr) return null;

    // Usamos moment.utc para que no importe la zona horaria de la máquina
    const m = moment.utc(
      dateStr,
      ['YYYY-MM-DDTHH:mm:ss.SSSSZ', 'YYYY-MM-DD', 'DD-MM-YYYY', 'YYYY-MM-DD HH:mm:ss'],
      true
    );

    if (!m.isValid()) return null;

    if (format) return m.format(format);

    // IMPORTANTE: Devolvemos un objeto con los datos UTC
    // pero "engañamos" al sistema creando una fecha local con esos mismos números
    return new Date(m.get('year'), m.get('month'), m.get('date'));
  } catch (error) {
    return null;
  }
};

// -------------------------------------------------------------------------------------- //
// -------------------------------------------------------------------------------------- //
// -------------------------------------------------------------------------------------- //

export const removeEmptyArrays = (data) => {
  Object.keys(data).forEach((key) => {
    const item = data[key];
    // see if this item is an array
    if (item && Array.isArray(item)) {
      // see if the array is empty
      if (item.length === 0) {
        // remove this item from the parent object
        // eslint-disable-next-line no-param-reassign
        delete data[key];
      } else {
        removeEmptyArrays(item);
      }
      // if this item is an object, then recurse into it to remove empty arrays in it too
    } else if (item && typeof item === 'object') {
      removeEmptyArrays(item);
    }
  });
  return data;
};

/**
 * Retorna un string con el valor del atributo separado por coma, omitendose datos sin valor.
 * Ej: "nombre1, nombre2, nombre3"
 * @param {*[]} array
 * @param {string} attribute
 */
export const listToString = (array, attribute) => {
  if (!array) {
    return '';
  }
  return array
    .map((obj) => {
      return obj[attribute];
    })
    .filter(Boolean)
    .join(', ');
};

/**
 * Convierte un objeto java.util.Date en Date
 * @param {java.util.Date} javaDate
 * @returns
 */
export const javaDateToJSDate = (javaDate) => {
  try {
    const jDate = javaDate['java.util.Date'] || javaDate;
    if (!jDate || Number.isNaN(jDate)) {
      return null;
    }
    return new Date(jDate);
  } catch (error) {
    return null;
  }
};

/**
 * Retorna true si el listado de objetos contiene al menos un atributo indefinido
 * @param {any[]} objArray
 * @param {String[]} attributeList
 * @returns
 */
export const hasUndefinedAttributes = (objArray, attributeList) => {
  let hasUndefined = false;
  for (let i = 0; i < attributeList.length; i++) {
    const attribute = attributeList[i];
    hasUndefined = objArray.some((elem) => elem[attribute] === undefined);
    if (hasUndefined) {
      break;
    }
  }
  return hasUndefined;
};

// -------------------------------------------------------------------------------------- //
// -------------------------------------------------------------------------------------- //
// -------------------------------------------------------------------------------------- //

export const parseCSS = (str) => {
  let obj = {};
  if (str) {
    const cssJson = `{"${str
      .trim()
      .replace(/\n/g, ' ')
      .replace(/"/g, '')
      .replace(/'/g, '')
      .replace(/=/g, ':')
      .replace(/-./g, (m) => m[1].toUpperCase())
      .replace(/; /g, '", "')
      .replace(/: /g, '": "')
      .replace(';', '')}"}`;
    try {
      obj = JSON.parse(cssJson);
    } catch {
      obj = {};
    }
  }
  return obj;
};

/**
 * Convierte una cadena en formato kebab-case a camelCase.
 * @param {string} kebabString La cadena en formato kebab-case.
 * @returns {string} La cadena convertida a camelCase.
 */
export const kebabToCamelCase = (kebabString) => {
  // Elimina guiones al principio y al final de la cadena si los hay
  const trimmedString = kebabString.replace(/^-+|-+$/g, '');
  // Convierte la cadena a camelCase
  return trimmedString.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Convierte una cadena de estilos CSS en un objeto de estilos de React.
 * @param {string} cssString La cadena de estilos CSS en línea.
 * @returns {Object} Un objeto de estilos de React.
 */
export const inlineStylesToReactStyles = (cssString) => {
  try {
    const styleList = cssString.split(';');
    const styles = styleList.reduce((stylesObj, style) => {
      // Divide cada par de propiedad-valor y elimina los espacios en blanco.
      const [property, value] = style.split(':').map((item) => item.trim());
      // Si hay una propiedad y un valor válidos, se agregan al objeto de estilos.
      if (property && value) {
        const camelCaseProperty = kebabToCamelCase(property);
        stylesObj[camelCaseProperty] = value;
      }
      return stylesObj;
    }, {});

    return styles;
  } catch (error) {
    return {};
  }
};

// -------------------------------------------------------------------------------------- //
// -------------------------------------------------------------------------------------- //
// -------------------------------------------------------------------------------------- //

/**
 * Generates a compareFn for Array.prototype.sort() that compares two objects
 * by the given propName.
 * @param {string} propName name of the numerical object property used to sort the array
 * @returns compareFn used by thesort function to decide object order in the array
 */
export const sortByNumProp = (propName) => {
  return (a, b) => {
    if (!a[propName] || !b[propName]) {
      // If the props were not defined in the objects,
      // these are not comparable, and cannot induce an ordering
      return 0;
    }
    return a[propName] - b[propName];
  };
};

/**
 * @param  {String} event code javascript
 * @param  {Function} updateVariableFromDOM
 */
export const onEvent = (event = '', updateVariableFromDOM = null) => {
  try {
    if (event && updateVariableFromDOM) {
      eval(event);
      updateVariableFromDOM();
    }

    if (event && !updateVariableFromDOM) {
      eval(event);
    }
  } catch (error) {
    toast.error(`${error.message} - ${event}`);
  }
};

/**
 * Retorna TRUE si es una URL valida y el protocolo es HTTP o HTTPS.
 * @param {String} stringURL
 * @returns
 */
export const isValidURL = (stringURL) => {
  try {
    const urlRegex = /^(https?:\/\/)([\w.-]+)(:?\d+)?(\/[\w-./?%&=]*)?$/;
    return urlRegex.test(stringURL);
  } catch (error) {
    return false;
  }
};

/**
 * Normaliza un string, eliminando todos los caracteres especiales y reemplazándolos con sus correspondientes letras.
 * Reemplaza los espacios con "_" y retorna el resultado en mayuscula.
 * @param {string} str - El string a ser normalizado.
 * @returns {string} El string normalizado.
 */
export const generateDescriptor = (str) => {
  return str
    .normalize('NFKD')
    .replace(/[^\w\s]/gi, '')
    .trim()
    .replace(/\s+/g, '_')
    .toUpperCase();
};

/**
 * Verifica es un objeto con atributos.
 * @param {any} obj - El objeto a verificar.
 * @returns {boolean} - true si un objeto y tiene atributos, de lo contrario false.
 */
export const isNonEmptyObject = (obj) => {
  try {
    return Object.keys(obj).length > 0;
  } catch (error) {
    return false;
  }
};

/**
 * Determina si un string es vacío o no.
 * @param {string} str - El string a evaluar
 * @returns {boolean} - Retorna true si el string es vacío y false si no lo es
 */
export const isBlankString = (str) => {
  try {
    const trimmedStr = str.replace(/\s/g, ''); // Eliminar todos los espacios en blanco
    return !trimmedStr;
  } catch (error) {
    return false;
  }
};

/**
 * Determina si un string es vacío o no.
 * Considera saltos de linea, espacios en blanco y algunas entidades de HTML.
 * @param {string} str - El string a evaluar
 * @returns {boolean} - Retorna true si el string es vacío y false si no lo es
 */
export const isBlankHTMLString = (str) => {
  try {
    return !str.replace(/\s|&nbsp;|&ensp;|&emsp;|&thinsp;+/g, '').length;
  } catch (error) {
    return false;
  }
};

/**
 * Extrae variables de un string.
 *
 * @param {string} str - El string que contiene variables.
 * @returns {Object} Un objeto que contiene las variables extraídas como claves con valor indefinido como valor por defecto.
 */
export const extractVariablesFromString = (str) => {
  try {
    if (isBlankString(str)) {
      return {};
    }
    const variablesMap = {};
    for (const match of str.matchAll(GLOBAL_VAR_REGEX)) {
      const variableName = match[1];
      variablesMap[variableName] = undefined;
    }
    return variablesMap;
  } catch (error) {
    return {};
  }
};

/**
 * Modifica los nombres de los atributos de un objeto agregando un prefijo y/o sufijo.
 * @param {Object} obj - El objeto cuyos atributos se modificarán.
 * @param {Object} options - Opciones para la modificación de los nombres de atributos.
 * @param {string} [options.prefix=''] - El prefijo a agregar a los nombres de los atributos. Valor por defecto: ''.
 * @param {string} [options.suffix=''] - El sufijo a agregar a los nombres de los atributos. Valor por defecto: ''.
 * @returns {Object} - El objeto con los nombres de atributos modificados.
 */
export const modifyAttributeNames = (obj, { prefix = '', suffix = '' }) => {
  if (!isNonEmptyObject(obj)) {
    return obj;
  }

  const modifiedObj = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const modifiedKey = `${prefix}${key}${suffix}`;
      modifiedObj[modifiedKey] = obj[key];
    }
  }

  return modifiedObj;
};

/**
 * Comprueba si el valor es un número válido o se puede convertir en número.
 * @param {*} value - El valor a verificar.
 * @returns {boolean} true si el valor es un número válido o se puede convertir en número, de lo contrario false.
 */
export const isValidNumber = (value) => {
  try {
    return !Number.isNaN(Number(value));
  } catch (error) {
    return false;
  }
};

/**
 * Genera un string normalizado en snake_case a partir de un string dado.
 * Elimina los caracteres especiales, reemplaza los espacios con guiones bajos y convierte a minúsculas.
 * @param {string} str - El string a ser normalizado.
 * @returns {string} El string normalizado en snake_case, o una cadena vacía si el parámetro no es válido.
 */
export const normalizeToSnakeCase = (str) => {
  if (!str || isBlankString(str)) {
    return ''; // Retorna una cadena vacía si el parámetro no es válido.
  }

  const normalizedString = str
    .normalize('NFKD') // Normaliza caracteres Unicode, como ñ a n.
    .replace(/[^\w\s]/gi, '') // Elimina caracteres especiales, excepto letras, dígitos y espacios.
    .replace(/\s+/g, '_') // Reemplaza espacios con guiones bajos.
    .replace(/_+/g, '_') // Reemplaza múltiples guiones bajos por uno solo.
    .toLowerCase();

  return normalizedString;
};

/**
 * Reemplaza variables de contexto por 0.
 * @param {string} string
 * @param {Array<Object>} contextVariables
 * @returns {string}
 */
export const cleanContextVariables = (string, contextVariables = []) => {
  if (!string || !string.trim() || !contextVariables || contextVariables.length === 0) {
    return string;
  }

  let cleanedString = string;
  contextVariables.forEach((variable) => {
    const varRegex = new RegExp(`\\${variable.varName}`, 'g');
    cleanedString = cleanedString.replace(varRegex, 0);
  });

  return cleanedString;
};

export const remplaceNullsWithEmptyString = (obj) => {
  if (Array.isArray(obj)) {
    // Si es un array, iteramos sobre sus elementos
    obj.forEach((element, index) => {
      if (element === null) {
        obj[index] = '';
      } else if (typeof element === 'object') {
        // Si el elemento es un objeto o array, llamamos a la funcion recursivamente
        remplaceNullsWithEmptyString(element);
      }
    });
  } else if (typeof obj === 'object') {
    // Si es un objeto, iteramos sobre sus propiedades
    // eslint-disable-next-line prefer-const
    for (let key in obj) {
      if (obj[key] === null) {
        obj[key] = '';
      } else if (typeof obj[key] === 'object') {
        // Si la propiedad es un objeto o array, llamamos a la funcion recursivamente
        remplaceNullsWithEmptyString(obj[key]);
      }
    }
  }
};

export const getInputType = (variableTypeId) => {
  if (variableTypeId === 2) return 'text';
  if ([3, 4, 5].includes(variableTypeId)) return 'number';
  return 'text';
};
